<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Produkty</span>
        <h3 class="page-title">Lista produktów</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button theme="success"
          @click.prevent="$router.push('/produkty/nowy')">
          <i class="material-icons">add</i>
          Dodaj produkt
        </d-button>
      </d-col>
    </d-row>

    <d-row
      v-if="loaded"
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="products-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          placeholder="Podaj nazwę produktu"
          v-model="filters.search" />

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">Szukaj</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">Nazwa produktu</th>
            <th scope="col" class="border-0">Kod produktu</th>
            <th scope="col" class="border-0">Kod ceny</th>
            <th scope="col" class="border-0">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(product, index) of products"
            :key="'product-' + index">
            <td class="align-middle">
              {{ product.name }}
              <template v-if="product.intro">
                <br>
                <small>{{ product.intro }}</small>
              </template>
            </td>
            <td class="align-middle">
              {{ product.code }}
            </td>
            <td class="align-middle">
              {{ product.price_code }}
            </td>
            <td
              class="align-middle"
              style="width: 200px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/produkty/edycja/' + product.id)"
                class="mr-2"
                title="Edytuj">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="info"
                :disabled="!!copyInProgress"
                @click.native="copyProduct(product.id)"
                class="mr-2"
                title="Kopiuj produkt">
                <div
                  v-if="copyInProgress === product.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
                <i
                  v-else
                  class="material-icons">
                  content_copy
                </i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="!!removeInProgress"
                @click.native="removeProduct(product.id)"
                title="Usuń">
                <div
                  v-if="removeInProgress === product.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
                <i
                  v-else
                  class="material-icons">
                  delete
                </i>
              </d-button>
            </td>
          </tr>
          <tr v-if="products.length === 0">
            <td
              colspan="5"
              class="text-center">
              Brak produktów do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import Vue from 'vue';

export default {
  name: 'products',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  data() {
    return {
      products: [],
      loaded: false,
      loadError: false,
      copyInProgress: false,
      removeInProgress: false,
      filters: {
        search: ''
      }
    };
  },
  mounted () {
    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        search: this.filters.search
      };

      ListUtils.loadItemsData(this, {
        endpoint: '/products/items?page=' + this.currentPage,
        listField: 'products',
        pagination: true,
        params: {
          where
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: ''
      };

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['products-filters-form'], this.debouncedLoadFilteredData);
    },
    copyProduct (productID) {
      this.copyInProgress = productID;

      FormUtils.submitInBackground(this, {
        endpoint: '/products/copy/' + productID,
        errorTitle: 'Wystąpił błąd',
        errorText: 'Kopiowanie produktu nie powiodło się. Spróbuj ponownie',
        successAction: (data) => {
          if (data.success) {
            this.copyInProgress = false;
            this.loadFilteredData();
          } else {
            Vue.swal({
              title: 'Wystąpił błąd',
              text: 'Kopiowanie produktu nie powiodło się. Spróbuj ponownie',
              icon: 'warning',
              confirmButtonText: 'OK'
            });

            this.copyInProgress = false;
            this.loadFilteredData();
          }
        },
        failAction: () => {
          this.copyInProgress = false;
          this.loadFilteredData();
        }
      });
    },
    removeProduct (productID) {
      this.removeInProgress = productID;

      ListUtils.removeItem(this, {
        endpointBase: '/products/delete/',
        id: productID,
        successTitle: 'Usunięto produkt',
        successText: 'Wybrany produkt został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć ten produkt?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie produktu nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    }
  }
};
</script>

